import React, { useMemo } from "react";
import { Dropdown } from "../Dropdown";
import { usePortalMenu } from "../../../hooks/usePortalMenu";
import { monthlyRepeatOptions } from "../../../utils/storyRefreshConstants";
import type { MonthlyRepeatOn } from "../../../utils/storyRefreshTypes";
import {
  Field,
  FieldLabel,
  HelperText,
} from "../shared/scheduleField.styles";

export interface MonthlyDayFieldProps {
  /** Recurrence option selected from the date-derived monthly choices. */
  value: MonthlyRepeatOn | null;
  startsOn: string;
  open: boolean;
  onSelect: (option: MonthlyRepeatOn) => void;
  onToggle: () => void;
  onClose: () => void;
  error?: string;
  showError?: boolean;
  className?: string;
}

/**
 * Monthly "Repeat on" — a Google Calendar-style dropdown derived from the
 * selected Starts on date.
 *
 * Stateless: the parent owns which dropdown is open so only one menu in the
 * modal can be open at a time.
 */
export const MonthlyDayField: React.FC<MonthlyDayFieldProps> = ({
  value,
  startsOn,
  open,
  onSelect,
  onToggle,
  onClose,
  error,
  showError = false,
  className,
}) => {
  const options = useMemo(() => monthlyRepeatOptions(startsOn), [startsOn]);
  const visibleError = showError && error ? error : undefined;

  const { triggerRef, menuRef, rect } = usePortalMenu<
    HTMLButtonElement,
    HTMLUListElement
  >({ open, onOutsideClick: onClose });

  return (
    <Field className={className}>
      <FieldLabel>Repeat on</FieldLabel>
      <Dropdown
        value={value}
        options={options}
        open={open}
        triggerRef={triggerRef}
        menuRef={menuRef}
        menuRect={rect}
        size="md"
        compact
        fullWidth
        placeholder="Pick a repeat option"
        invalid={Boolean(visibleError)}
        ariaLabel="Day of the month"
        onToggle={onToggle}
        onSelect={(v) => {
          onSelect(v as MonthlyRepeatOn);
          onClose();
        }}
      />
      {visibleError && <HelperText $error>{visibleError}</HelperText>}
    </Field>
  );
};

export default MonthlyDayField;
